import { IconInfoCircle } from '@tabler/icons-react';
import { useAuth } from '../../../../auth/context/AuthProvider';
import React, { useCallback, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import useIndexPayment from '../../../../services/payment/hooks/useIndexPayment';
import Loader from '../../../../shared/components/Loader';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import { Controller, useForm } from 'react-hook-form';
import { CreatePayment, CreatePaymentSchema } from '../../../../services/payment/schema/CreatePaymentSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import useCreatePayment from '../../../../services/payment/hooks/useCreatePayment';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import CurrencyInputRef from '../../../../shared/components/form/CurrencyInputRef';
import DateInput from '../../../../shared/components/form/DateInput';
import RemovePayment from './RemovePayment';

const InfoTooltip = ({ message }: { message: string }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{message}</Tooltip>}
    >
        <span style={{ cursor: 'pointer' }} className="text-muted ms-1">
            <IconInfoCircle size={16} />
        </span>
    </OverlayTrigger>
);

type Props = {
    reservationId?: string;
    onClose?: () => void;
}

const PaymentSection: React.FC<Props> = ({ reservationId }) => {
    const { hasRole, hasPermission } = useAuth();
    // Real data fetching and mutation logic would go here

    const { data: reservationData, isFetching: isReservationFetching, isSuccess: isReservationSuccess } = useShowBooking({
        bookingId: reservationId || ''
    })

    const { data: paymentsData, isFetching, isSuccess } = useIndexPayment({
        filters: {
            booking_room_id: reservationId || '',
        },
        include: "property,bookingRoom,bookingRoom.roomType,bookingRoom.room"
    });

    const isLoading = isFetching || isReservationFetching;
    const isSuccessLoading = isSuccess && isReservationSuccess;

    // Form submission logic would go here

    const { control, handleSubmit, register, reset, formState: { errors } } = useForm<CreatePayment>({
        resolver: zodResolver(CreatePaymentSchema),
        defaultValues: {
            date_payment: new Date().toISOString().split('T')[0],
            payment_type: 'Cash',
        },
    });

    useEffect(() => {
        if (reservationId) {
            reset({
                date_payment: new Date().toISOString().split('T')[0],
                payment_type: 'Cash',
                booking_room_id: reservationId,
            });
        }
    }, [reservationId]);

    const { mutate, isPending } = useCreatePayment();
    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: CreatePayment) => {
        if (!hasRole('dev') && !hasPermission('booking_update')) {
            toast.error("You don't have permission to do this action");
            return;
        }

        toast.loading('Creating payment...', { id: 'create-payment' });

        mutate({
            ...values,
            booking_room_id: reservationId || '',
        }, {
            onSuccess: async () => {
                await queryClient.refetchQueries();
                toast.success('Payment created successfully', { id: 'create-payment' });
                reset();
            },
            onError: () => {
                toast.error('Failed to create payment', { id: 'create-payment' });
            },
        });
    }, [hasRole, hasPermission, mutate]);

    // remove payment logic would go here
    const buttonIsDisabled = isPending;

    return (
        <div className="container-fluid mt-4">
            {isLoading && (
                <Loader />
            )}
            {!isLoading && isSuccessLoading && (
                <>
                    <div className="row align-items-start mb-3 flex-column flex-md-row">
                        <div className="col mb-3 mb-md-0">
                            <h5 className="mb-0 fs-3 fw-bold text-primary">
                                Add Payment for Booking ID: <strong>#{reservationData.data.invoice_number}</strong>
                            </h5>
                        </div>
                        <div className="col-auto text-start">
                            <div className="mb-2">
                                <strong>Total Invoice: </strong>
                                {(Number(reservationData.data.total_rate)).toLocaleString('id-ID')} IDR
                            </div>
                            <div>
                                <strong>Total Payments: </strong>
                                {(reservationData.data.total_payments)} IDR
                            </div>
                        </div>
                    </div>

                    <form className="row gy-3 flex-column flex-md-row align-items-end mb-3" onSubmit={handleSubmit(onSubmit)}>
                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Payment Date
                                <InfoTooltip message="The payment date corresponding to the transaction." />
                            </label>
                            <DateInput
                                className="form-control"
                                defaultValue={new Date().toISOString().split('T')[0]}
                                {...register('date_payment')}
                                disabled={isPending}
                            />
                            {errors.date_payment && (
                                <div className="text-danger mt-1">
                                    {errors.date_payment.message}
                                </div>
                            )}
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Payment Type
                                <InfoTooltip message="The payment type used for the transaction." />
                            </label>
                            <Controller
                                name="payment_type"
                                control={control}
                                render={({ field }) => (
                                    <select className="form-select" {...field} disabled={isPending}>
                                        <option value="ATM">ATM</option>
                                        <option value="Transfer">Bank Transfer</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Credit Card - AMEX">Credit Card - AMEX</option>
                                        <option value="Credit Card - DISCOVER">Credit Card - DISCOVER</option>
                                        <option value="Credit Card - MASTERCARD">Credit Card - MASTERCARD</option>
                                        <option value="Credit Card - OTHERS">Credit Card - OTHERS</option>
                                        <option value="Credit Card - VISA">Credit Card - VISA</option>
                                        <option value="Debit Card">Debit Card</option>
                                        <option value="DOKU PAYMENT">DOKU PAYMENT</option>
                                        <option value="Ota Payment">OTA Payment</option>
                                        <option value="PAYPAL">PAYPAL</option>
                                    </select>
                                )}
                            />
                            {errors.payment_type && (
                                <div className="text-danger mt-1">
                                    {errors.payment_type.message}
                                </div>
                            )}
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Amount
                                <InfoTooltip message="Total amount paid." />
                            </label>
                            <Controller
                                name="amount"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CurrencyInputRef
                                        name={field.name}
                                        value={Number(field.value) ?? 0}
                                        onChange={(_e, rawValue) => {
                                            field.onChange(rawValue);
                                        }}
                                        error={fieldState.error?.message}
                                        ref={field.ref} // ini akan bekerja karena kita sudah pakai forwardRef
                                    />
                                )}
                            />
                        </div>

                        <div className="col-12 col-md-auto">
                            <label className="form-label d-flex align-items-center gap-1">
                                Surcharge
                                <InfoTooltip message="Additional fee for the payment type." />
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Empty"
                                {...register('surcharge')}
                                disabled={isPending}
                            />
                            {errors.surcharge && (
                                <div className="text-danger mt-1">
                                    {errors.surcharge.message}
                                </div>
                            )}
                        </div>

                        {/* <div className="col-12 col-md-auto">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="extraOnly"
                                    id="extraOnly"
                                />
                                <label className="form-check-label d-flex align-items-center gap-1" htmlFor="extraOnly">
                                    Extra Only
                                    <InfoTooltip message="Check this if the payment is for extra charges only." />
                                </label>
                            </div>
                        </div> */}

                        <div className="col-12 col-md-auto">
                            <button type='submit' className="btn btn-primary w-100 w-md-auto" disabled={buttonIsDisabled}>
                                Add Payment!
                            </button>
                        </div>
                    </form>

                    <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                            <thead className="table-light">
                                <tr>
                                    <th>Payment Date</th>
                                    <th>Payment Type</th>
                                    <th>Amount</th>
                                    <th>Surcharge</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paymentsData && paymentsData.data.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                            No payments found for this booking.
                                        </td>
                                    </tr>
                                )}
                                {paymentsData && paymentsData.data.length > 0 && paymentsData.data.map((payment, index) => (
                                    <tr key={index}>
                                        <td>{payment.date_payment}</td>
                                        <td>{payment.payment_type}</td>
                                        <td>{Number(payment.amount).toLocaleString('id-ID')} IDR</td>
                                        <td>{Number(payment.surcharge)}</td>
                                        <td>
                                            <RemovePayment paymentId={payment.id} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default PaymentSection;
